import React, { useRef, useState } from 'react';
import { Camera, User, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const ProfilePhotoUpload = ({ 
  currentPhoto, 
  onPhotoChange, 
  size = 'md',
  name = '',
  disabled = false 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef(null);

  const sizeClasses = {
    sm: 'h-12 w-12',
    md: 'h-20 w-20',
    lg: 'h-32 w-32'
  };

  const iconSizes = {
    sm: 'h-5 w-5',
    md: 'h-8 w-8',
    lg: 'h-12 w-12'
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setIsUploading(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = () => {
        // Create image to resize
        const img = new Image();
        img.onload = () => {
          // Resize to 200x200 max
          const canvas = document.createElement('canvas');
          const maxSize = 200;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxSize) {
              height *= maxSize / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width *= maxSize / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Get compressed base64
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8);
          onPhotoChange(compressedBase64);
          setIsUploading(false);
          toast.success('Photo updated!');
        };
        img.onerror = () => {
          toast.error('Failed to load image');
          setIsUploading(false);
        };
        img.src = reader.result;
      };
      reader.onerror = () => {
        toast.error('Failed to read image');
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload photo');
      setIsUploading(false);
    }

    // Clear input
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleRemovePhoto = (e) => {
    e.stopPropagation(); // Prevent triggering file input
    onPhotoChange(''); // Clear the photo
    toast.success('Photo removed!');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="relative inline-block">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || isUploading}
      />
      
      <div 
        className={`${sizeClasses[size]} rounded-full overflow-hidden bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity relative`}
        onClick={() => !disabled && !isUploading && inputRef.current?.click()}
      >
        {isUploading ? (
          <Loader2 className={`${iconSizes[size]} text-white animate-spin`} />
        ) : currentPhoto ? (
          <img 
            src={currentPhoto} 
            alt={name || 'Profile'} 
            className="h-full w-full object-cover"
            onError={(e) => {
              // Handle broken images gracefully
              e.target.style.display = 'none';
              e.target.parentElement.classList.add('fallback-initials');
            }}
          />
        ) : name ? (
          <span className={`text-white font-semibold ${size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-lg' : 'text-sm'}`}>
            {getInitials(name)}
          </span>
        ) : (
          <User className={`${iconSizes[size]} text-white`} />
        )}
      </div>

      {/* Camera button to upload */}
      {!disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
          className="absolute bottom-0 right-0 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full p-1.5 shadow-lg transition-colors"
          disabled={isUploading}
          title="Upload new photo"
        >
          <Camera className="h-3 w-3" />
        </button>
      )}

      {/* Remove button - only show if there's a photo */}
      {!disabled && currentPhoto && (
        <button
          type="button"
          onClick={handleRemovePhoto}
          className="absolute top-0 right-0 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-lg transition-colors"
          disabled={isUploading}
          title="Remove photo"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

export default ProfilePhotoUpload;
